import React, { useState, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';

import { Box, xcss } from '@atlaskit/primitives';
import { CommentEditor as AkCommentEditor } from '@atlaskit/editor-core/appearance-editor-comment';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { CommentEditor } from '@confluence/comment';
import type { CommentData } from '@confluence/comments-data';
import { CommentType, useCommentsDataActions } from '@confluence/comments-data';
import {
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components';
import {
	onSaveReply,
	ViewReplyOptions,
	getAdfForQuickReplyMention,
} from '@confluence/comments-panel';
import type { InlineCommentReply, NewReplyVars } from '@confluence/inline-comments-queries';
import { CreateInlineReplyMutation } from '@confluence/inline-comments-queries';
import { CreateGeneralReplyMutation } from '@confluence/page-comments-queries/entry-points/CreateGeneralReplyMutation.graphql';
import type {
	CreateGeneralReplyMutationData,
	CreateGeneralReplyMutationVariables,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralReplyMutation';
import { ReactionsContext, useCommentsContentActions } from '@confluence/comment-context';
import { useUnreadCommentsActions } from '@confluence/unread-comments';
import { useSessionData } from '@confluence/session-data';
import { useSearchSessionId } from '@confluence/search-session';
import { useCommentsPanel } from '@confluence/comments-panel-utils';
import type { PageMode } from '@confluence/page-utils/entry-points/enums';
import type { PageInfoNode } from '@confluence/page-info';
import { fg } from '@confluence/feature-gating';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';

const commentEditorWrapperStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const commentEditorCustomStyles = {
	marginLeft: token('space.300'),
	// Be careful updating this, it can cause the branch to jump slightly when switching from the button to the editor
	height: '24px',
	marginRight: token('space.050'),
};

export const ObjectCommentReplyEditor = ({
	pageInfo,
	pageMode,
	setReplyView,
	parentComment,
	numOfReplies,
	threadKey,
	replyToId,
	isOffline,
}: {
	pageInfo: PageInfoNode | null;
	pageMode: PageMode;
	setReplyView: (view: ViewReplyOptions) => void;
	parentComment: CommentData;
	numOfReplies: number;
	threadKey: string;
	replyToId: string;
	isOffline?: boolean;
}) => {
	const replyEditorContainerId = `object-comment-reply-editor-${replyToId}`;

	const [createInlineReplyFn] = useMutation<
		{ replyInlineComment: InlineCommentReply },
		{ input: NewReplyVars; pageId: string }
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		CreateInlineReplyMutation,
	);

	const [createGeneralReplyFn] = useMutation<
		CreateGeneralReplyMutationData,
		CreateGeneralReplyMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		CreateGeneralReplyMutation,
	);

	const { isReactionsEnabled } = useContext(ReactionsContext);

	const { onChange, resetContentChanged } = useCommentsContentActions();

	const { cloudId } = useSessionData();
	const [{ searchSessionId }] = useSearchSessionId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addReplyToCommentThread } = useCommentsDataActions();
	const { updateReadCommentsListState } = useUnreadCommentsActions();
	const [{ replyToCommentId, replyToCommentUser }, { setReplyToCommentId }] = useCommentsPanel();

	const [, setCommentForEdit] = useState('');

	const experienceTracker = useContext(ExperienceTrackerContext);

	const pageId = pageInfo?.id ?? '';
	const pageType = pageInfo?.type ?? '';
	const spaceId = pageInfo?.space?.id ?? '';
	const commentType = parentComment.type;

	// User page permissions
	const operations = pageInfo?.operations || [];

	// The user can upload media only if they have update permissions for the page
	const hasMediaUploadPermissions = operations.some(
		(op) => op?.operation === 'update' && op?.targetType === pageType,
	);

	const onEditorReady = () => {
		let experienceName = REPLY_TO_INLINE_COMMENT_LOAD_EXPERIENCE;

		if (commentType === CommentType.GENERAL) {
			if (fg('confluence_frontend_separate_add_page_comment')) {
				experienceName = REPLY_TO_PAGE_COMMENT_LOAD_EXPERIENCE;
			} else {
				experienceName = ADD_PAGE_COMMENT_LOAD_EXPERIENCE;
			}
		}

		experienceTracker.succeed({
			name: experienceName,
		});

		const commentReplyContainer = document.getElementById(replyEditorContainerId);

		if (!commentReplyContainer) {
			return;
		}

		// Check whether the bottom of the comment reply container is visible
		const isBottomOutOfView =
			commentReplyContainer.getBoundingClientRect().bottom > window.innerHeight;

		// Only scroll if we currently can't see the bottom of the comment reply container. It's annoying to always scroll
		if (isBottomOutOfView) {
			commentReplyContainer.scrollIntoView({
				behavior: 'smooth',
				block: 'end',
				inline: 'nearest',
			});
		}
	};

	const commentEditorCommentType = commentType === CommentType.GENERAL ? 'page' : commentType;
	const commentEditorPageMode = pageMode === 'edit' ? 'edit' : 'view';

	return (
		replyToCommentId && (
			<WithCurvedBranchingStyle showDefaultStyles customStyles={commentEditorCustomStyles}>
				<Box
					xcss={commentEditorWrapperStyles}
					id={replyEditorContainerId}
					testId={replyEditorContainerId}
				>
					<CommentEditor
						pageId={pageId}
						pageType={pageType}
						content={getAdfForQuickReplyMention(replyToCommentUser)}
						appearance="comment"
						EditorComponent={AkCommentEditor}
						onSaveComment={(adf, onSuccess) => {
							setReplyView(ViewReplyOptions.ALL);
							setReplyToCommentId();
							resetContentChanged();

							return onSaveReply({
								pageId,
								parentCommentId: replyToId,
								cloudId,
								adf,
								onSuccess,
								pageMode,
								setCommentForEdit,
								createAnalyticsEvent,
								pageType,
								experienceTracker,
								createInlineReplyFn,
								createGeneralReplyFn,
								addReplyToCommentThread,
								threadKey,
								updateReadCommentsListState,
								commentType,
								searchSessionId,
								isReactionsEnabled,
								analyticsSource: AnalyticsSource.PAGE_FOOTER,
							});
						}}
						onContentChange={onChange}
						onEditorReady={onEditorReady}
						commentMode="reply"
						commentType={
							fg('confluence_frontend_page_comment_comment_type')
								? commentEditorCommentType
								: 'inline'
						}
						spaceId={fg('confluence_frontend_page_comment_comment_type') ? spaceId : ''}
						showCancelButton
						useNewWarningModal
						hideWatchCheckbox
						expandEditor
						pageMode={
							fg('confluence_frontend_page_comment_comment_type') ? commentEditorPageMode : 'view'
						}
						hasMediaUploadPermissions={hasMediaUploadPermissions}
						shouldDisplayCommentReplyPrompts={false}
						topLevelCommentId={parentComment.id}
						shouldWarnOnInternalNavigation
						commentThreadLength={numOfReplies + 1}
						onCancelComment={() => {
							setReplyToCommentId();
						}}
						isCommentsPanel={false}
						isFooterComment
						wasRemovedByAnotherUser={parentComment.wasRemovedByAnotherUser}
						isOffline={isOffline}
					/>
				</Box>
			</WithCurvedBranchingStyle>
		)
	);
};
